import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost'
    ? 'http://localhost:8001/api/crew'
    : `${window.location.protocol}//${window.location.hostname.replace('crew-frontend', 'crew-api')}/api/crew`
);

export interface ApiError {
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

export interface CrewMember {
  id: number;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  hire_date: string;
  position: string;
  rank: string;
  base: string;
  status: string;
  license_number?: string;
  license_expiry?: string;
  medical_class?: string;
  medical_expiry?: string;
  passport_number?: string;
  passport_expiry?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Qualification {
  id: number;
  crew_member_id: number;
  qualification_type: string;
  aircraft_type?: string;
  issue_date: string;
  expiry_date: string;
  issuing_authority: string;
  certificate_number?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface RosterAssignment {
  id: number;
  crew_member_id: number;
  flight_id?: string;
  duty_type: string;
  start_time: string;
  end_time: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface BidRequest {
  id: number;
  crew_member_id: number;
  roster_period_start: string;
  roster_period_end: string;
  bid_type: string;
  priority: number;
  preferences: Record<string, any>;
  status: string;
  submitted_at: string;
  processed_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FRMSRecord {
  id: number;
  crew_member_id: number;
  duty_date: string;
  duty_hours: number;
  flight_hours: number;
  rest_hours: number;
  fatigue_score?: number;
  sleep_quality?: string;
  alertness_level?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: number;
  user_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  changes: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
}

export interface FlightSchedule {
  id: number;
  flight_number: string;
  operating_date: string;
  departure_airport: string;
  arrival_airport: string;
  scheduled_departure: string;
  scheduled_arrival: string;
  block_time_minutes: number;
  aircraft_type: string;
  max_seats?: number;
  is_active: boolean;
}

export interface ScheduleStats {
  total_schedules: number;
  active_schedules: number;
  unique_routes: number;
  date_range_start?: string;
  date_range_end?: string;
  aircraft_types: string[];
  airports: string[];
}

export interface UploadResult {
  success: boolean;
  message: string;
  total_rows: number;
  imported: number;
  skipped: number;
  errors: string[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantResponse {
  success: boolean;
  response?: string;
  error?: string;
  mentioned_crew: any[];
  model?: string;
  usage?: any;
}

export interface QuickAction {
  label: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface CrewDestinations {
  crew_id: number;
  crew_name: string;
  destinations: Array<{
    date: string;
    flight_number: string;
    departure: { code: string; coordinates: any };
    arrival: { code: string; coordinates: any };
  }>;
}

export interface NetworkMapData {
  airports: Array<{
    code: string;
    name: string;
    city: string;
    lat: number;
    lng: number;
    flight_count: number;
  }>;
  routes: Array<{
    from: string;
    to: string;
    flight_count: number;
    from_coords: [number, number];
    to_coords: [number, number];
  }>;
  total_routes: number;
  total_airports: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// ---- IROPS (irregular operations crew recovery) ----------------------------

export type DisruptionType =
  | 'crew_unavailable'
  | 'flight_cancelled'
  | 'flight_delayed'
  | 'station_closure'
  | 'generic';

export interface DisruptionEvent {
  id: number;
  reference: string;
  disruption_type: DisruptionType;
  description: string;
  raw_input?: string | null;
  affected_crew_ids: string[];
  affected_pairing_ids: string[];
  station?: string | null;
  delay_hours?: number | null;
  window_start: string;
  window_end: string;
  status: 'open' | 'recovering' | 'resolved' | 'dismissed';
  created_by?: string | null;
  created_at?: string | null;
  plans?: RecoveryPlan[];
}

export interface RecoveryPlanChange {
  pairing_id: string;
  position: string;
  kind: 'swap' | 'added' | 'removed';
  from_crew?: string | null;
  to_crew?: string | null;
  from_crew_id?: string | null;
  to_crew_id?: string | null;
}

export interface RecoveryPlanCrew {
  crew_id: string;
  name: string;
  position: string;
  is_reserve: boolean;
  deadhead: boolean;
  ftl_28d_margin_hours: number;
}

export interface RecoveryPlanAssignment {
  pairing_id: string;
  pairing_number: string;
  origin: string;
  start?: string | null;
  crew: Record<string, RecoveryPlanCrew>;
}

export interface RecoveryPlan {
  id: number;
  disruption_id: number;
  rank: number;
  objective_cost: number;
  status: 'proposed' | 'approved' | 'rejected' | 'superseded';
  summary: {
    optimal?: boolean;
    uncovered_positions: number;
    assignments_changed: number;
    reserves_called: number;
    deadheads: number;
    reserves?: string[];
  };
  assignments: RecoveryPlanAssignment[];
  changes: RecoveryPlanChange[];
  uncovered: Array<{ pairing_id: string; position: string }>;
  approved_by?: string | null;
  approved_at?: string | null;
}

export interface DisruptionProposal {
  disruption_type: DisruptionType;
  description: string;
  affected_crew_ids: string[];
  affected_pairing_ids: string[];
  station?: string | null;
  delay_hours?: number | null;
  raw_input?: string;
  source: 'llm' | 'heuristic';
  confidence: string;
  window_start: string;
  window_end: string;
}

export interface RecoverResult {
  disruption: DisruptionEvent;
  plans: RecoveryPlan[];
}

export interface PlanExplanation {
  rationale: string;
  crew_message: string;
  source: 'llm' | 'template';
}

class ApiService {
  public client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('crew_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
          localStorage.removeItem('crew_token');
          localStorage.removeItem('crew_user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        const apiError: ApiError = {
          message: error.response?.data?.message || error.message || 'An error occurred',
          statusCode: error.response?.status,
          errors: error.response?.data?.errors,
        };
        return Promise.reject(apiError);
      }
    );
  }

  async login(employeeId: string, password: string): Promise<{ user: any; token: string }> {
    const response = await this.client.post('/auth/login', {
      employee_id: employeeId,
      password,
    });
    const { user, token } = response.data;
    localStorage.setItem('crew_token', token);
    localStorage.setItem('crew_user', JSON.stringify(user));
    return { user, token };
  }

  logout(): void {
    localStorage.removeItem('crew_token');
    localStorage.removeItem('crew_user');
  }

  getCurrentUser(): any | null {
    const raw = localStorage.getItem('crew_user');
    return raw ? JSON.parse(raw) : null;
  }

  isAuthenticated(): boolean {
    return Boolean(localStorage.getItem('crew_token'));
  }

  // Crew Members
  async getCrewMembers(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    position?: string;
    base?: string;
  }): Promise<PaginatedResponse<CrewMember>> {
    const response = await this.client.get<PaginatedResponse<CrewMember>>('/personnel', { params });
    return response.data;
  }

  async getCrewMember(id: number): Promise<CrewMember> {
    const response = await this.client.get<CrewMember>(`/personnel/${id}`);
    return response.data;
  }

  async createCrewMember(data: Partial<CrewMember>): Promise<CrewMember> {
    const response = await this.client.post<CrewMember>('/personnel', data);
    return response.data;
  }

  async updateCrewMember(id: number, data: Partial<CrewMember>): Promise<CrewMember> {
    const response = await this.client.put<CrewMember>(`/personnel/${id}`, data);
    return response.data;
  }

  async deleteCrewMember(id: number): Promise<void> {
    await this.client.delete(`/personnel/${id}`);
  }

  // Qualifications
  async getQualifications(crewMemberId?: number): Promise<Qualification[]> {
    const url = crewMemberId
      ? `/qualifications/${crewMemberId}`
      : '/qualifications/expiring';
    const params = crewMemberId ? undefined : { days: 365 };
    const response = await this.client.get<any[]>(url, { params });
    const raw = Array.isArray(response.data) ? response.data : [];
    return raw.map((q: any) => ({
      id: q.id,
      crew_member_id: q.crew_member_id ?? q.crew_id,
      qualification_type: q.qualification_type,
      aircraft_type: q.aircraft_type ?? q.qualification_name,
      issue_date: q.issue_date,
      expiry_date: q.expiry_date,
      issuing_authority: q.issuing_authority ?? q.authority,
      certificate_number: q.certificate_number ?? q.document_number,
      status: q.status,
      notes: q.notes,
      created_at: q.created_at ?? '',
      updated_at: q.updated_at ?? '',
    }));
  }

  async createQualification(data: Partial<Qualification>): Promise<Qualification> {
    const crewId = data.crew_member_id;
    const response = await this.client.post<Qualification>(`/qualifications/${crewId}`, data);
    return response.data;
  }

  async updateQualification(id: number, data: Partial<Qualification>): Promise<Qualification> {
    const crewId = data.crew_member_id;
    const response = await this.client.put<Qualification>(`/qualifications/${crewId}/${id}`, data);
    return response.data;
  }

  async deleteQualification(id: number, crewMemberId?: number): Promise<void> {
    if (!crewMemberId) throw new Error('crew_member_id required to delete qualification');
    await this.client.delete(`/qualifications/${crewMemberId}/${id}`);
  }

  // Roster
  async getRosterAssignments(params?: {
    crew_member_id?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<RosterAssignment[]> {
    const apiParams: any = { ...params };
    if (apiParams.crew_member_id) {
      apiParams.crew_id = apiParams.crew_member_id;
      delete apiParams.crew_member_id;
    }
    const response = await this.client.get('/roster', { params: apiParams });
    const raw = Array.isArray(response.data) ? response.data : (response.data?.assignments ?? []);
    return raw.map((a: any) => ({
      id: a.id,
      crew_member_id: a.crew_member_id,
      flight_id: a.flight_number ?? a.flight_id,
      duty_type: a.assignment_type ?? a.duty_type,
      start_time: a.duty_start ?? a.departure_time ?? `${a.assignment_date}T00:00:00`,
      end_time: a.duty_end ?? a.arrival_time ?? `${a.assignment_date}T23:59:59`,
      status: a.status ?? 'scheduled',
      notes: a.notes,
      created_at: a.created_at ?? '',
      updated_at: a.updated_at ?? '',
    }));
  }

  async createRosterAssignment(data: Partial<RosterAssignment>): Promise<RosterAssignment> {
    const response = await this.client.post<RosterAssignment>('/roster', data);
    return response.data;
  }

  async updateRosterAssignment(id: number, data: Partial<RosterAssignment>): Promise<RosterAssignment> {
    const response = await this.client.put<RosterAssignment>(`/roster/${id}`, data);
    return response.data;
  }

  async deleteRosterAssignment(id: number): Promise<void> {
    await this.client.delete(`/roster/${id}`);
  }

  // Bidding
  async getBidRequests(params?: {
    crew_member_id?: number;
    status?: string;
  }): Promise<BidRequest[]> {
    const response = await this.client.get('/bids', { params });
    const raw = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
    return raw;
  }

  async createBidRequest(data: Partial<BidRequest>): Promise<BidRequest> {
    const response = await this.client.post<BidRequest>('/bids', data);
    return response.data;
  }

  async updateBidRequest(id: number, data: Partial<BidRequest>): Promise<BidRequest> {
    const response = await this.client.put<BidRequest>(`/bids/${id}`, data);
    return response.data;
  }

  async processBidRequest(id: number): Promise<BidRequest> {
    const response = await this.client.post<BidRequest>('/bids/process', { bid_ids: [id] });
    return response.data;
  }

  // FRMS : backend exposes /frms/dashboard (aggregates) and /frms/history/{crew_id}.
  // No general "list records" endpoint, so for the admin overview we synthesize
  // a per-day records list from the active alerts when a specific crew isn't given.
  async getFRMSRecords(params?: {
    crew_member_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<FRMSRecord[]> {
    if (params?.crew_member_id) {
      const response = await this.client.get(`/frms/history/${params.crew_member_id}`);
      const raw = Array.isArray(response.data) ? response.data : (response.data?.history ?? []);
      return raw;
    }
    return [];
  }

  async createFRMSRecord(data: Partial<FRMSRecord>): Promise<FRMSRecord> {
    const response = await this.client.post<FRMSRecord>('/frms/report', data);
    return response.data;
  }

  async updateFRMSRecord(id: number, _data: Partial<FRMSRecord>): Promise<FRMSRecord> {
    throw new Error(`FRMS record update not supported by backend (id=${id})`);
  }

  // Audit : backend has /audit/results (compliance audit results, not user action log).
  async getAuditLogs(params?: {
    user_id?: string;
    entity_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<AuditLog>> {
    const response = await this.client.get('/audit/results', {
      params: {
        page: params?.page,
        page_size: params?.per_page,
      },
    });
    const body = response.data ?? {};
    const items = body.results ?? body.data ?? [];
    return {
      data: items.map((r: any) => ({
        id: r.id,
        user_id: String(r.auditor_id ?? r.user_id ?? ''),
        action: r.action ?? r.outcome ?? 'audit',
        entity_type: r.entity_type ?? r.audit_type ?? 'compliance',
        entity_id: String(r.entity_id ?? r.id ?? ''),
        changes: r.changes ?? r.findings ?? {},
        ip_address: r.ip_address,
        user_agent: r.user_agent,
        timestamp: r.timestamp ?? r.created_at ?? r.audited_at ?? '',
      })),
      total: body.total ?? items.length,
      page: body.page ?? 1,
      per_page: body.page_size ?? body.per_page ?? items.length,
      total_pages: body.total_pages ?? 1,
    };
  }

  // Dashboard Stats
  async getDashboardStats(): Promise<{
    total_crew: number;
    active_crew: number;
    total_qualifications: number;
    expiring_soon: number;
    roster_assignments: number;
    pending_bids: number;
  }> {
    const response = await this.client.get('/stats/dashboard');
    return response.data;
  }

  // Flight Schedules
  async getFlightSchedules(params?: {
    start_date?: string;
    end_date?: string;
    departure_airport?: string;
    arrival_airport?: string;
    aircraft_type?: string;
    flight_number?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<FlightSchedule[]> {
    const response = await this.client.get<FlightSchedule[]>('/schedules', { params });
    return response.data;
  }

  async getScheduleStats(): Promise<ScheduleStats> {
    const response = await this.client.get<ScheduleStats>('/schedules/stats');
    return response.data;
  }

  async uploadFlightSchedule(file: File, replaceExisting: boolean = false): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<UploadResult>('/schedules/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        replace_existing: replaceExisting,
      },
    });
    return response.data;
  }

  async deleteAllSchedules(confirm: boolean = false): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete('/schedules', {
      params: { confirm },
    });
    return response.data;
  }

  async getFlightScheduleById(id: number): Promise<FlightSchedule> {
    const response = await this.client.get<FlightSchedule>(`/schedules/${id}`);
    return response.data;
  }

  // AI Assistant
  async chatWithAssistant(message: string, conversationHistory: ChatMessage[] = []): Promise<AssistantResponse> {
    const response = await this.client.post<AssistantResponse>('/assistant/chat', {
      message,
      conversation_history: conversationHistory,
    });
    return response.data;
  }

  async findCoverage(crewName: string, date?: string): Promise<AssistantResponse> {
    const response = await this.client.post<AssistantResponse>('/assistant/coverage', {
      crew_name: crewName,
      date,
    });
    return response.data;
  }

  async getQuickActions(): Promise<{ actions: QuickAction[] }> {
    const response = await this.client.get('/assistant/quick-actions');
    return response.data;
  }

  // Map & Destinations
  async getCrewDestinations(crewId: number, startDate?: string, endDate?: string): Promise<CrewDestinations> {
    const response = await this.client.get<CrewDestinations>(`/assistant/crew/${crewId}/destinations`, {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  }

  async getNetworkMap(): Promise<NetworkMapData> {
    const response = await this.client.get<NetworkMapData>('/assistant/network-map');
    return response.data;
  }

  async getAirports(): Promise<Record<string, any>> {
    const response = await this.client.get('/assistant/airports');
    return response.data;
  }

  // IROPS : Irregular Operations Crew Recovery
  async iropsIntake(text: string, windowStart: string, windowEnd: string): Promise<DisruptionProposal> {
    const response = await this.client.post<DisruptionProposal>('/irops/intake', {
      text,
      window_start: windowStart,
      window_end: windowEnd,
    });
    return response.data;
  }

  async listDisruptions(status?: string): Promise<DisruptionEvent[]> {
    const response = await this.client.get<DisruptionEvent[]>('/irops/disruptions', {
      params: status ? { status_filter: status } : undefined,
    });
    return response.data;
  }

  async getDisruption(id: number): Promise<DisruptionEvent> {
    const response = await this.client.get<DisruptionEvent>(`/irops/disruptions/${id}`);
    return response.data;
  }

  async createDisruption(data: {
    disruption_type: DisruptionType;
    description: string;
    window_start: string;
    window_end: string;
    affected_crew_ids?: string[];
    affected_pairing_ids?: string[];
    station?: string | null;
    delay_hours?: number | null;
    raw_input?: string | null;
  }): Promise<DisruptionEvent> {
    const response = await this.client.post<DisruptionEvent>('/irops/disruptions', data);
    return response.data;
  }

  async recoverDisruption(
    id: number,
    options?: { num_plans?: number; allow_deadhead?: boolean; time_limit_seconds?: number },
  ): Promise<RecoverResult> {
    const response = await this.client.post<RecoverResult>(`/irops/disruptions/${id}/recover`, options ?? {});
    return response.data;
  }

  async dismissDisruption(id: number): Promise<DisruptionEvent> {
    const response = await this.client.post<DisruptionEvent>(`/irops/disruptions/${id}/dismiss`);
    return response.data;
  }

  async explainPlan(planId: number): Promise<PlanExplanation> {
    const response = await this.client.post<PlanExplanation>(`/irops/plans/${planId}/explain`);
    return response.data;
  }

  async approvePlan(planId: number): Promise<RecoveryPlan> {
    const response = await this.client.post<RecoveryPlan>(`/irops/plans/${planId}/approve`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
